import { ref, computed, Ref } from 'vue'
import type { FeatureCollection } from 'geojson'
import { orderBy } from 'lodash'
import { bbox as turfBbox } from '@turf/turf'
import download from 'downloadjs'

import { IGeoImageRepository } from '../../../../@core/lidar/domain/repositories/IGeoImage.repository'
import { IStorageRepository } from '../../../../@core/lidar/domain/repositories/IStorage.repository'
import { IFarmRepository } from '../../../../@core/farm/domain/repositories/IFarm.repository'
import { GeoImageRepositoryApiAdapter } from '../../../../@core/lidar/infra/repositories/GeoImageRepositoryApi.adapter'
import { StorageRepositoryApiAdapter } from '../../../../@core/lidar/infra/repositories/StorageRepositoryApi.adapter'
import { FarmRepositoryApiAdapter } from '../../../../@core/farm/infra/repositories/FarmRepositoryApi.adapter'
import { GeoImage } from '../../../../@core/lidar/domain/entities/GeoImage'
import { GeoImageTypeEnum } from '../../../../@core/lidar/domain/enums/GeoImageType.enum'
import { Farm } from '../../../../@core/farm/domain/entities/Farm'
import { FarmDto } from '../../../../@core/lidar/domain/dtos/Farm.dto'
import { useMessages } from '../../../../composables/useMessages'
import { StorageUsage } from '../../../../@core/lidar/domain/entities/StorageUsage'
import { GeoTiffBandExtractorProvider } from '../providers/GeoTiffBandExtractor.provider'
import { GeoTiffBandResultDto } from '../dtos/GeoTiffBandResult.dto'
import { BandTypeEnum } from '../../../../@core/lidar/domain/enums/BandType.enum'
import { registryApi } from '../../../../services/clients/AxiosClients'
import { IFarmMapRepository } from '../../../../@core/gis/domain/repositories/IFarmMap.repository'
import { FarmMapRepository } from '../../../../@core/gis/infra/repositories/FarmMap.repository'
import { FarmMapDto } from '../../../../@core/gis/domain/dtos/FarmMap.dto'
import { FarmClipBufferProvider } from '../providers/FarmClipBuffer.provider'
import { GeoImageOverlayProvider, GeoImageOverlay } from '../providers/GeoImageOverlay.provider'

type TableHeader = {
	key: string
	value: string
	isActive: boolean
}

type GeoImageFormModel = {
	id?: string
	name: string
	description: string
	farm: FarmDto
	acquisitionDate: Date | null
	imageType: string
	image?: File
	bandCount?: number
	pixel?: string
}

function createEmptyModel(): GeoImageFormModel {
	return {
		id: '',
		name: '',
		description: '',
		farm: { name: '', id: '' },
		acquisitionDate: null,
		imageType: '',
		image: undefined,
		bandCount: undefined,
		pixel: undefined,
	}
}

export class GeoImagesViewModel {
	private _geoImageRepository: IGeoImageRepository
	private _storageRepository: IStorageRepository
	private _farmRepository: IFarmRepository
	private _farmMapRepository: IFarmMapRepository
	private _messages = useMessages()

	// State refs
	modelList: Ref<GeoImage[]> = ref([])
	model: Ref<GeoImageFormModel> = ref(createEmptyModel())
	farmOptions: Ref<Farm[]> = ref([])
	showForm = ref(false)
	titleForm = ref('')
	textFilter = ref('')
	sortBy: Ref<string | undefined> = ref(undefined)
	sortType: Ref<string | undefined> = ref(undefined)
	disableSaveButton = ref(false)
	isEdit = ref(false)
	showExclusionAlert = ref(false)
	intention: Ref<any> = ref({})
	storageUsage = ref<StorageUsage>()
	showStorageRequestDialog = ref(false)
	extractedBands = ref<GeoTiffBandResultDto | null>(null)
	isExtractingBands = ref(false)
	isLoadingEditImage = ref(false)
	createdImageId = ref('')
	/** Registro criado cuja remocao falhou; removido antes do proximo cadastro. */
	pendingDeletionId = ref('')
	farmMap: Ref<FarmMapDto | null> = ref(null)
	clipBuffer: Ref<FeatureCollection | null> = ref(null)
	imageOverlay: Ref<GeoImageOverlay | null> = ref(null)
	imageCoverage: Ref<GeoImageOverlay | null> = ref(null)
	isLoadingValidation = ref(false)
	/** Preenchido pela etapa de validacao quando o Mapbox nao desenha a imagem. */
	imageRenderFailed = ref(false)
	formStep = ref(0)
	bandMapping = ref<Record<string, string>>({
		[BandTypeEnum.RED]: '',
		[BandTypeEnum.GREEN]: '',
		[BandTypeEnum.BLUE]: '',
		[BandTypeEnum.NIR]: '',
	})
	tableHeaders: Ref<TableHeader[]> = ref([
		{ key: 'code', value: 'Código', isActive: true },
		{ key: 'name', value: 'Nome', isActive: true },
		{ key: 'farm.name', value: 'Fazenda', isActive: true },
		{ key: 'formattedAcquisitionDate', value: 'Data de Aquisição', isActive: true },
		{ key: 'imageType', value: 'Tipo de Imagem', isActive: true },
		{ key: 'pixelLabel', value: 'Pixel', isActive: true },
		{ key: 'bandsCount', value: 'Camadas', isActive: true },
		{ key: 'description', value: 'Descrição', isActive: true },
	])
	headersSelecionados: Ref<string[]> = ref([
		'code',
		'name',
		'farm.name',
		'formattedAcquisitionDate',
		'imageType',
		'pixelLabel',
		'bandsCount',
		'description',
	])
	tableConfiguration = ref({
		statusTable: 'Carregando...',
		subtitleCardPropertie: 'description',
		titleCardPropertie: 'name',
	})
	atualStartIndex = ref(0)
	atualEndIndex = ref(0)
	quantityPerPage = ref(10)

	// Computed
	sortedData = computed(() => {
		return orderBy(this.modelList.value, this.sortBy.value, [this.sortType.value as any])
	})

	isDeleteButtonDisabled = computed(() => {
		return this.modelList.value.filter((f) => f.isCheckedToDelete).map((m) => m.id).length === 0
	})

	hasFarmShape = computed(() => {
		const farm = this.farmMap.value?.fazenda
		if (!farm) return false
		return farm.type === 'FeatureCollection' ? farm.features.length > 0 : true
	})

	/** Mede contra o buffer, que e a extensao que o processamento recorta. */
	imageIntersectsFarm = computed(() => {
		const overlay = this.imageOverlay.value
		if (!overlay) return false
		const area = this.clipBuffer.value ?? this.farmMap.value?.fazenda ?? null
		return GeoImageOverlayProvider.intersectsArea(overlay.bounds, area as never)
	})

	/**
	 * Toda imagem que a etapa de validacao nao consegue mostrar sobre a fazenda
	 * tambem nao serve para o processamento: TIFF sem georreferenciamento ou com
	 * EPSG nao suportado (sem overlay), fazenda sem shape, imagem em outra regiao
	 * ou falha do Mapbox ao desenhar a camada.
	 */
	canConfirmValidation = computed(() => {
		return (
			!this.disableSaveButton.value &&
			!this.isLoadingValidation.value &&
			this.imageOverlay.value !== null &&
			this.hasFarmShape.value &&
			this.imageIntersectsFarm.value &&
			!this.imageRenderFailed.value
		)
	})

	modelsIdWithCheckedToDelete = computed(() => {
		return this.modelList.value.filter((f) => f.isCheckedToDelete).map((m) => m.id)
	})

	modelsToBeShowedAndTotal = computed(() => {
		let modelsFiltred = this.sortedData.value

		const textFilter = this.textFilter.value.toLowerCase()
		modelsFiltred = modelsFiltred.filter((item) => {
			const atualModel = Object.values(item).join(' ')
			return atualModel.toLowerCase().includes(textFilter)
		})

		return {
			items: modelsFiltred.slice(this.atualStartIndex.value, this.atualEndIndex.value),
			total: modelsFiltred.length,
		}
	})

	headersAtivos = computed(() => {
		const ativos = this.tableHeaders.value.filter((f) =>
			this.headersSelecionados.value.includes(f.key),
		)
		ativos.forEach((f) => {
			f.isActive = true
		})
		return ativos
	})

	kebabOptions = computed(() => {
		return [{ value: 'editar', label: 'Editar' }]
	})

	requiresBandDefinition = computed(() => {
		const type = this.model.value.imageType
		return type === GeoImageTypeEnum.DRONE || type === GeoImageTypeEnum.SATELITE
	})

	isStorageFull = computed(() => (this.storageUsage.value?.usedPercent ?? 0) >= 100)

	camposObrigatoriosPreenchidos = computed(() => {
		return (
			!!this.model.value.name &&
			!!this.model.value.acquisitionDate &&
			!!this.model.value.farm.id &&
			!!this.model.value.imageType &&
			!!this.model.value.image
		)
	})

	imageTypeOptions = computed(() => {
		return [
			{ label: 'Drone', value: GeoImageTypeEnum.DRONE },
			{ label: 'LiDAR', value: GeoImageTypeEnum.LIDAR },
			{ label: 'Satélite', value: GeoImageTypeEnum.SATELITE },
			{ label: 'MDT', value: GeoImageTypeEnum.MDT },
		]
	})

	breadcrumbCrumbs = computed(() => {
		const crumbs = [{ name: 'cadastro e upload', step: 0 }]
		if (this.requiresBandDefinition.value) {
			crumbs.push({ name: 'definição das bandas', step: 1 })
		}
		return crumbs
	})

	bandDropdownOptions = computed(() => {
		if (!this.extractedBands.value) {
			return {
				[BandTypeEnum.RED]: [],
				[BandTypeEnum.GREEN]: [],
				[BandTypeEnum.BLUE]: [],
				[BandTypeEnum.NIR]: [],
			}
		}

		const allOptions = this.extractedBands.value.bands.map((band) => ({
			value: band.name,
			label: band.name,
		}))

		const bandTypes = [BandTypeEnum.RED, BandTypeEnum.GREEN, BandTypeEnum.BLUE, BandTypeEnum.NIR]
		const result: Record<string, Array<{ value: string; label: string }>> = {}

		for (const bandType of bandTypes) {
			const selectedByOthers = bandTypes
				.filter((bt) => bt !== bandType)
				.map((bt) => this.bandMapping.value[bt])
				.filter((v) => v !== '')

			result[bandType] = allOptions.filter((opt) => !selectedByOthers.includes(opt.value))
		}

		return result
	})

	constructor() {
		this._geoImageRepository = new GeoImageRepositoryApiAdapter(registryApi)
		this._storageRepository = new StorageRepositoryApiAdapter(registryApi)
		this._farmRepository = new FarmRepositoryApiAdapter(registryApi)
		this._farmMapRepository = new FarmMapRepository(registryApi)
	}

	async fetchAll() {
		try {
			const geoImages = await this._geoImageRepository.getAll()
			this.modelList.value = geoImages
		} catch (error) {
			console.log(error)
			this._messages.addMessage({
				description: 'Não foi possível carregar as imagens',
				icon: 'fas fa-exclamation-circle',
				type: 'error',
			})
		} finally {
			this.tableConfiguration.value.statusTable = 'Nenhum registro encontrado'
		}
	}

	async fetchStorageUsage() {
		try {
			this.storageUsage.value = await this._storageRepository.getStorageUsage()
		} catch {
			this._messages.addMessage({
				description: 'Não foi possível carregar o uso de armazenamento',
				icon: 'fas fa-exclamation-circle',
				type: 'error',
			})
		}
	}

	async fetchFarms() {
		try {
			this.farmOptions.value = await this._farmRepository.getAll()
		} catch (error) {
			console.log(error)
			this._messages.addMessage({
				description: 'Não foi possível carregar as fazendas',
				icon: 'fas fa-exclamation-circle',
				type: 'error',
			})
		}
	}

	async handleFileUpload(files: FileList | File[] | null) {
		console.log(files)
		if (!files || files.length === 0) {
			this.model.value = { ...this.model.value, image: undefined }
			this.extractedBands.value = null
			return
		}

		const file = files[0]
		this.model.value.image = file
		this.extractedBands.value = null

		const isTiff =
			file.name.toLowerCase().endsWith('.tif') ||
			file.name.toLowerCase().endsWith('.tiff') ||
			file.name.toLowerCase().endsWith('.geotiff')

		if (!isTiff) return

		this.isExtractingBands.value = true

		try {
			const result = await GeoTiffBandExtractorProvider.extract(file)

			if (!result.isGeoreferenced) {
				this.model.value = { ...this.model.value, image: undefined }
				this.extractedBands.value = null
				this._messages.addMessage({
					description: 'A imagem carregada não está georreferenciada.',
					icon: 'fas fa-exclamation-circle',
					type: 'error',
				})
				return
			}

			this.extractedBands.value = result
		} catch {
			this._messages.addMessage({
				description: 'Não foi possível extrair as bandas do arquivo GeoTIFF',
				icon: 'fas fa-exclamation-circle',
				type: 'error',
			})
		} finally {
			this.isExtractingBands.value = false
		}
	}

	async create() {
		const m = this.model.value
		const pixel = this._buildPixel()

		const geoImage = new GeoImage(
			m.name,
			m.farm,
			m.acquisitionDate!,
			m.imageType as GeoImageTypeEnum,
			'',
			pixel,
			this.bandMapping.value,
			m.description,
			undefined,
			undefined,
			m.image,
		)

		try {
			if (!(await this._removePendingRegistration())) {
				this._messages.addMessage({
					description:
						'Um cadastro anterior não pôde ser removido. Exclua-o pela listagem antes de concluir este.',
					icon: 'fas fa-exclamation-circle',
					type: 'error',
				})
				return
			}

			this.createdImageId.value = await this._geoImageRepository.create(geoImage)
			await this.fetchStorageUsage()
			await this._loadValidationData()
			this.formStep.value = 2
		} catch (error) {
			console.error('[GeoImages.create] falha ao cadastrar imagem', error)
			this._messages.addMessage({
				description: 'Não foi possível cadastrar a imagem',
				icon: 'fas fa-exclamation-circle',
				type: 'error',
			})
		} finally {
			this.disableSaveButton.value = false
		}
	}

	async update() {
		const m = this.model.value
		const pixel = this._buildPixel()

		const geoImage = new GeoImage(
			m.name,
			m.farm,
			m.acquisitionDate!,
			m.imageType as GeoImageTypeEnum,
			'',
			pixel,
			this.bandMapping.value,
			m.description,
			undefined,
			m.id,
			m.image,
		)

		try {
			await this._geoImageRepository.update(geoImage)
			this.showForm.value = false
			await this.fetchAll()
			await this.fetchStorageUsage()
			this._messages.addMessage({
				description: 'Imagem atualizada com sucesso',
				icon: 'far fa-check-circle',
				type: 'success',
			})
		} catch {
			this._messages.addMessage({
				description: 'Não foi possível atualizar a imagem',
				icon: 'fas fa-exclamation-circle',
				type: 'error',
			})
		} finally {
			this.disableSaveButton.value = false
		}
	}

	async deleteGeoImages() {
		const idsToDelete = this.modelsIdWithCheckedToDelete.value.filter(
			(id): id is string => id !== undefined,
		)
		if (idsToDelete.length === 0) return

		try {
			await this._geoImageRepository.delete(idsToDelete)
			this.modelList.value = this.modelList.value.filter(
				(m) => !idsToDelete.includes(m.id as string),
			)
			this.showExclusionAlert.value = false
			await this.fetchStorageUsage()
			this._messages.addMessage({
				description: 'Imagens excluídas com sucesso',
				icon: 'far fa-check-circle',
				type: 'success',
			})
		} catch {
			this._messages.addMessage({
				description: 'Não foi possível excluir as imagens',
				icon: 'fas fa-exclamation-circle',
				type: 'error',
			})
		}
	}

	saveClick() {
		this.disableSaveButton.value = true
		if (this.isEdit.value) {
			this.update()
		} else {
			this.create()
		}
	}

	advanceToStep1() {
		this.formStep.value = 1
	}

	goBackToStep0() {
		this.formStep.value = 0
	}

	showNewModelForm() {
		this.cleanModel()
		this.titleForm.value = 'Cadastrar Imagem'
		this.isEdit.value = false
		this.changeFormAndCloseAllActions()
	}

	showEditModelForm(model: GeoImage) {
		this.model.value = {
			id: model.id ?? '',
			name: model.name,
			description: model.description ?? '',
			farm: { ...model.farm },
			acquisitionDate: model.acquisitionDate,
			imageType: model.imageType,
			image: model.image,
			bandCount: model.bandsCount,
			pixel: model.pixel,
		}
		this.bandMapping.value = {
			[BandTypeEnum.RED]: model.bands[BandTypeEnum.RED] ?? '',
			[BandTypeEnum.GREEN]: model.bands[BandTypeEnum.GREEN] ?? '',
			[BandTypeEnum.BLUE]: model.bands[BandTypeEnum.BLUE] ?? '',
			[BandTypeEnum.NIR]: model.bands[BandTypeEnum.NIR] ?? '',
		}
		this.titleForm.value = 'Editar Imagem'
		this.isEdit.value = true
		this.changeFormAndCloseAllActions()
		this._loadImageAndExtractBands(model)
	}

	changeFormAndCloseAllActions() {
		this.showForm.value = !this.showForm.value
		this.formStep.value = 0
	}

	toggleStorageRequestDialog() {
		this.showStorageRequestDialog.value = !this.showStorageRequestDialog.value
	}

	async requestStorageIncrease() {
		try {
			await this._storageRepository.requestStorageIncrease()
			this.showStorageRequestDialog.value = false
			this._messages.addMessage({
				description: 'Solicitação de aumento de armazenamento enviada com sucesso',
				icon: 'far fa-check-circle',
				type: 'success',
			})
		} catch {
			this._messages.addMessage({
				description: 'Não foi possível solicitar aumento de armazenamento',
				icon: 'fas fa-exclamation-circle',
				type: 'error',
			})
		}
	}

	toggleExclusionAlert() {
		this.showExclusionAlert.value = !this.showExclusionAlert.value
	}

	/**
	 * TEMPORARIO (diagnostico): registra no console o endpoint e a resposta do
	 * download da imagem no blob storage. Remover quando o contrato estiver
	 * validado — ver tambem o log do tamanho do blob em cada chamador.
	 */
	private async _fetchImage(context: string, model: GeoImage): Promise<Response> {
		const endpoint = model.imageLink

		console.log(`[GeoImages.${context}] GET`, endpoint)

		const response = await fetch(endpoint)

		console.log(`[GeoImages.${context}] response`, {
			status: response.status,
			statusText: response.statusText,
			ok: response.ok,
			contentType: response.headers.get('content-type'),
			contentLength: response.headers.get('content-length'),
			url: response.url,
		})

		return response
	}

	/**
	 * Remove o registro que sobrou de um cancelamento cujo delete falhou. Aborta
	 * o cadastro se nao conseguir: seguir adiante criaria um duplicado, que e
	 * justamente o que o cancelamento deveria ter evitado.
	 */
	private async _removePendingRegistration(): Promise<boolean> {
		const pendingId = this.pendingDeletionId.value
		if (!pendingId) return true

		try {
			await this._geoImageRepository.delete([pendingId])
			this.pendingDeletionId.value = ''
			return true
		} catch (error) {
			console.error('[GeoImages] falha ao remover registro pendente', error)
			return false
		}
	}

	/**
	 * Monta as camadas da etapa de validacao: shape da fazenda, o buffer de
	 * recorte e a imagem georreferenciada.
	 */
	private async _loadValidationData() {
		this.isLoadingValidation.value = true
		this.farmMap.value = null
		this.clipBuffer.value = null
		this.imageOverlay.value = null
		this.imageCoverage.value = null
		this.imageRenderFailed.value = false

		try {
			const farmId = this.model.value.farm.id
			if (farmId) {
				const farmMap = await this._farmMapRepository.getByFarmId(farmId)
				this.farmMap.value = farmMap
				this.clipBuffer.value = FarmClipBufferProvider.build(farmMap.fazenda)
			}

			if (this.extractedBands.value) {
				// Recorta pelo buffer, nao pelo shape: o recorte de 50m e o que o
				// processamento usa, entao a imagem e mostrada na mesma extensao.
				this.imageOverlay.value = GeoImageOverlayProvider.build(
					this.extractedBands.value,
					this.clipBuffer.value,
				)
				this.imageCoverage.value = GeoImageOverlayProvider.buildCoverageMask(
					this.extractedBands.value,
					this.clipBuffer.value,
				)
			}

			this._logOverlayFit()
		} catch (error) {
			console.error('[GeoImages] falha ao montar a etapa de validação', error)
			this._messages.addMessage({
				description: 'Não foi possível carregar o mapa de validação',
				icon: 'fas fa-exclamation-circle',
				type: 'error',
			})
		} finally {
			this.isLoadingValidation.value = false
		}
	}

	/**
	 * TEMPORARIO (diagnostico): confere se a imagem e a fazenda ocupam a mesma
	 * regiao do mundo. Uma imagem georreferenciada longe da fazenda aparece fora
	 * do enquadramento, ou some por inteiro quando recortada pelo buffer.
	 */
	private _logOverlayFit() {
		const overlay = this.imageOverlay.value

		if (!overlay) {
			console.warn('[GeoImages] overlay da imagem não foi gerado — ver mensagem acima do motivo')
			return
		}

		const buffer = this.clipBuffer.value
		if (!buffer || !buffer.features.length) {
			console.warn('[GeoImages] sem buffer da fazenda; imagem exibida sem recorte', {
				imagem: overlay.bounds,
			})
			return
		}

		const fazenda = turfBbox(buffer as never) as [number, number, number, number]
		const [iw, is, ie, ineg] = overlay.bounds
		const [fw, fs, fe, fn] = fazenda
		const sobrepoe = iw < fe && ie > fw && is < fn && ineg > fs

		console.log('[GeoImages] enquadramento imagem x fazenda', {
			imagem: overlay.bounds,
			fazenda,
			sobrepoe,
			larguraImagemGraus: +(ie - iw).toFixed(6),
			larguraFazendaGraus: +(fe - fw).toFixed(6),
		})

		if (!sobrepoe) {
			console.error(
				'[GeoImages] a imagem não se sobrepõe à fazenda: o georreferenciamento do TIFF aponta para outra região',
			)
		}
	}

	/** Conclui o cadastro: o registro ja existe desde a etapa anterior. */
	async confirmValidation() {
		if (!this.canConfirmValidation.value) return

		this._closeFormAfterValidation()
		await this.fetchAll()
		this._messages.addMessage({
			description: 'Imagem cadastrada com sucesso',
			icon: 'far fa-check-circle',
			type: 'success',
		})
	}

	/**
	 * Desiste do cadastro: o registro foi criado antes da validacao, entao
	 * cancelar exige remove-lo.
	 */
	async cancelValidation() {
		const createdId = this.createdImageId.value

		if (!createdId) {
			console.error('[GeoImages.cancelValidation] sem id do registro criado; nada a remover')
			this._backToFormStart()
			await this.fetchAll()
			this._messages.addMessage({
				description:
					'O registro não pôde ser removido automaticamente. Exclua-o pela listagem antes de cadastrar novamente.',
				icon: 'fas fa-exclamation-circle',
				type: 'error',
			})
			return
		}

		this.disableSaveButton.value = true

		try {
			await this._geoImageRepository.delete([createdId])
			this._messages.addMessage({
				description: 'Cadastro desfeito. Ajuste os dados e conclua novamente.',
				icon: 'far fa-check-circle',
				type: 'success',
			})
		} catch (error) {
			console.error('[GeoImages.cancelValidation] falha ao remover registro', error)
			// Retem o id: sem ele, concluir de novo criaria um duplicado.
			this.pendingDeletionId.value = createdId
			this._messages.addMessage({
				description:
					'Não foi possível remover o registro agora. Uma nova tentativa será feita ao concluir o cadastro.',
				icon: 'fas fa-exclamation-circle',
				type: 'error',
			})
		} finally {
			this.disableSaveButton.value = false
			this._backToFormStart()
			await this.fetchAll()
			await this.fetchStorageUsage()
		}
	}

	/**
	 * Volta para a primeira etapa preservando o que o usuario ja preencheu: o
	 * registro foi removido, entao concluir de novo cria um novo cadastro.
	 */
	private _backToFormStart() {
		this.createdImageId.value = ''
		this.farmMap.value = null
		this.clipBuffer.value = null
		this.imageOverlay.value = null
		this.imageCoverage.value = null
		this.imageRenderFailed.value = false
		this.formStep.value = 0
		this.showForm.value = true
	}

	private _closeFormAfterValidation() {
		this.showForm.value = false
		this.cleanModel()
	}

	cleanModel() {
		this.model.value = createEmptyModel()
		this.createdImageId.value = ''
		this.farmMap.value = null
		this.clipBuffer.value = null
		this.imageOverlay.value = null
		this.imageCoverage.value = null
		this.imageRenderFailed.value = false
		this.extractedBands.value = null
		this.isExtractingBands.value = false
		this.formStep.value = 0
		this.bandMapping.value = {
			[BandTypeEnum.RED]: '',
			[BandTypeEnum.GREEN]: '',
			[BandTypeEnum.BLUE]: '',
			[BandTypeEnum.NIR]: '',
		}
	}

	private async _loadImageAndExtractBands(model: GeoImage) {
		this.isLoadingEditImage.value = true

		try {
			const response = await this._fetchImage('editar', model)
			const blob = await response.blob()
			console.log('[GeoImages.editar] blob', { size: blob.size, type: blob.type })
			const extension = model.fileName.substring(model.fileName.lastIndexOf('.'))
			const fileNameWithoutExt = model.fileName.substring(0, model.fileName.lastIndexOf('.'))
			const parts = fileNameWithoutExt.split('_')
			const displayName = parts.slice(1, -1).join('_') + extension
			const file = new File([blob], displayName, { type: blob.type })
			this.model.value.image = file

			const isTiff =
				model.fileName.toLowerCase().endsWith('.tif') ||
				model.fileName.toLowerCase().endsWith('.tiff') ||
				model.fileName.toLowerCase().endsWith('.geotiff')

			if (isTiff) {
				this.extractedBands.value = await GeoTiffBandExtractorProvider.extract(file)
			}
		} catch {
			this._messages.addMessage({
				description: 'Não foi possível carregar a imagem existente',
				icon: 'fas fa-exclamation-circle',
				type: 'error',
			})
		} finally {
			this.isLoadingEditImage.value = false
		}
	}

	private _initBandMappingDefaults() {
		const bandTypeByIndex = [
			BandTypeEnum.RED,
			BandTypeEnum.GREEN,
			BandTypeEnum.BLUE,
			BandTypeEnum.NIR,
		]
		const bands = this.extractedBands.value?.bands ?? []

		this.bandMapping.value = {
			[BandTypeEnum.RED]: '',
			[BandTypeEnum.GREEN]: '',
			[BandTypeEnum.BLUE]: '',
			[BandTypeEnum.NIR]: '',
		}

		bands.forEach((band, index) => {
			const bandType = bandTypeByIndex[index]
			if (bandType) {
				this.bandMapping.value[bandType] = band.name
			}
		})
	}

	private _buildPixel(): string {
		if (!this.extractedBands.value) return ''
		const { width, height } = this.extractedBands.value.metadata
		return `${width}x${height}`
	}

	setStartEnd(startIndex: number) {
		this.atualStartIndex.value = startIndex
		this.atualEndIndex.value = startIndex + this.quantityPerPage.value
	}

	setQuantityPerPage(qty: number) {
		this.quantityPerPage.value = qty
	}

	downloadCSV() {
		const headers = [
			'Nome',
			'Fazenda',
			'Data de Aquisição',
			'Tipo de Imagem',
			'Pixel',
			'Camadas',
			'Descrição',
		]
		const items = this.modelList.value

		const csv = [
			headers,
			...items.map((item) => [
				item.name,
				item.farm?.name ?? '',
				item.acquisitionDate ? new Date(item.acquisitionDate).toLocaleDateString() : '',
				item.imageType,
				item.pixel,
				item.bandsCount,
				item.description ?? '',
			]),
		]
			.map((m) => m.join(';'))
			.join('\r\n')

		download(csv, 'export_geo_images.csv', 'text/csv')
	}
}
